import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast, ToastProps, ToastType } from './Toast';

interface ToastContextValue {
  showToast: (options: Omit<ToastProps, 'id' | 'onClose'>) => string;
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

interface ToastState extends Omit<ToastProps, 'onClose'> {}

let toastIdCounter = 0;

export function ToastProvider({
  children,
  maxToasts = 5,
  position = 'top-right',
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const showToast = useCallback(
    (options: Omit<ToastProps, 'id' | 'onClose'>) => {
      const id = `toast-${++toastIdCounter}`;

      setToasts((prev) => {
        const newToasts = [...prev, { ...options, id }];
        if (newToasts.length > maxToasts) {
          return newToasts.slice(-maxToasts);
        }
        return newToasts;
      });

      return id;
    },
    [maxToasts]
  );

  const createToastFn = useCallback(
    (type: ToastType) => (title: string, message?: string) => {
      return showToast({ type, title, message });
    },
    [showToast]
  );

  const positionClasses: Record<string, string> = {
    'top-right': 'top-0 right-0',
    'top-left': 'top-0 left-0',
    'bottom-right': 'bottom-0 right-0',
    'bottom-left': 'bottom-0 left-0',
    'top-center': 'top-0 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
  };

  const value: ToastContextValue = {
    showToast,
    success: createToastFn('success'),
    error: createToastFn('error'),
    warning: createToastFn('warning'),
    info: createToastFn('info'),
    dismissToast,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="assertive"
        className={`pointer-events-none fixed z-50 flex flex-col gap-2 p-4 w-full max-w-sm ${positionClasses[position]}`}
      >
        {toasts.map((toast) => (
          <Toast key={toast.id} {...toast} onClose={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
